import { useCallback, useEffect, useState } from 'react'

import { getDiscounts } from '@/api/clarityLens'
import { ScalrTableColumn } from '@/components/customComponents'
import { formatMetric } from '@/hooks/clarity/util'
import {
  DiscountItemResponseModel,
  DiscountsItemsModel,
} from '@/types/clarityLens'

const BASE_DISCOUNTS_ITEMS_COLUMNS: ScalrTableColumn[] = [
  {
    header: 'Discount Type',
    accessorKey: 'discountType',
    type: 'text',
  },
  {
    header: '# of Discounts Applied',
    accessorKey: 'numberOfDiscountsApplied',
    type: 'number',
  },
  {
    header: 'Total Discount Amount',
    accessorKey: 'totalDiscountAmount',
    type: 'money',
  },
  {
    header: 'Discount %',
    accessorKey: 'discountPercentage',
    type: 'percentage',
  },
]

export default function useDiscountsItems(
  startDate: string,
  endDate: string,
  branches: string,
  itemGroupName: string,
  sizeFilter: string,
  temperatureFilter: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [discountsItemsData, setDiscountsItemsData] =
    useState<DiscountsItemsModel[]>()

  const fetchAllData = useCallback(async () => {
    if (!itemGroupName) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data } = await fetchDiscountsItemsData(
        branches,
        startDate,
        endDate,
        itemGroupName,
        sizeFilter,
        temperatureFilter
      )

      setDiscountsItemsData(data)
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [
    branches,
    startDate,
    endDate,
    itemGroupName,
    sizeFilter,
    temperatureFilter,
  ])

  const refetch = useCallback(() => {
    console.log('Refetching discounts items...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    discountsItemsData,
    BASE_DISCOUNTS_ITEMS_COLUMNS,
    loading,
    error,
    refetch,
  }
}

async function fetchDiscountsItemsData(
  branches: string,
  startDate: string,
  endDate: string,
  itemGroupName: string,
  sizeFilter: string,
  temperatureFilter: string
): Promise<{
  data: DiscountsItemsModel[]
}> {
  try {
    const response = await getDiscounts(
      branches,
      startDate,
      endDate,
      itemGroupName,
      sizeFilter,
      temperatureFilter
    )

    const data: DiscountsItemsModel[] = Object.entries(response).map(
      ([keyName, value]) => {
        return convertData(keyName, value)
      }
    )

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  keyName: string,
  value: DiscountItemResponseModel
): DiscountsItemsModel {
  const { count, amount, discountPercentage } = value

  const baseModel: DiscountsItemsModel = {
    discountType: keyName,
    numberOfDiscountsApplied: formatMetric(count, 0),
    totalDiscountAmount: formatMetric(amount, 2),
    // TODO: Remove * 10 when API is updated
    discountPercentage: formatMetric(discountPercentage * 10, 2),
  }

  return baseModel
}
